// composables/useImpersonationTransition.ts
// Drives the full-screen loading overlay shown while entering/exiting
// impersonation. Both transitions end in a `window.location.href` full reload
// (so every Pinia store re-inits with the new identity), so the overlay state
// must survive that reload — we hand it off via sessionStorage and resume it
// on the freshly loaded page until it has had a tick to paint.
import { useState } from '#imports'

const KEY = '__impersonation_transition__'

export function useImpersonationTransition() {
  const visible = useState<boolean>('impersonation-transition-visible', () => false)
  const message = useState<string>('impersonation-transition-message', () => 'Cargando…')

  /** Show the overlay now AND persist it so it resumes after the full reload. */
  function begin(msg: string) {
    message.value = msg
    visible.value = true
    if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(KEY, msg)
  }

  /** On a freshly reloaded page: re-show the overlay if a transition was in flight. */
  function resumeAfterReload(): boolean {
    if (typeof sessionStorage === 'undefined') return false
    const msg = sessionStorage.getItem(KEY)
    if (!msg) return false
    message.value = msg
    visible.value = true
    return true
  }

  /** Hide the overlay and clear the persisted flag (on completion or failure). */
  function end() {
    visible.value = false
    if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(KEY)
  }

  return { visible, message, begin, resumeAfterReload, end }
}
